import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Chip, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import CustomButton from '../reusable/CustomButton';
import useSchools from '../hooks/useSchools';
import { MetricOption } from '../../interfaces/MetricsInterface';
import {
  postMetricsQuery,
  postSchoolMetricsQuery,
  postSchoolSurveyMetricsQuery,
  postSurveyMetricsQuery,
  splitMetricValue,
} from '../../services/metricsService';
import ChatCopyButton from './ChatCopyButton';
import ChatMetricResult from './ChatMetricResult';
import { columnsOf, metricValueToRows, rowsToTsv, MetricRow } from './metricTable';
import { copyPlainText } from '../../utils/clipboard';
import CloseIcon from '../assets/close-icon.svg';
import { ReactComponent as ChipDeleteIcon } from '../assets/chip-delete-icon.svg';

export interface DataExplorerRequest {
  metric: string;
}

interface ChatDataExplorerProps {
  eoaId: string;
  accessToken: string;
  metricOptions: MetricOption[];
  request?: DataExplorerRequest | null;
  onClose: () => void;
}

interface ScopeResult {
  id: string;
  label: string;
  value?: unknown;
  error?: string;
}

interface QueryResults {
  columnLabel: string;
  scopes: ScopeResult[];
}

const ENTIRE_DISTRICT = 'all-schools';
const ALL_SCHOOLS_LABEL = 'All schools';

export const resolveSchoolSelection = (next: string[], previous: string[]): string[] =>
  next.includes(ENTIRE_DISTRICT) && !previous.includes(ENTIRE_DISTRICT)
    ? [ENTIRE_DISTRICT]
    : next.filter((id) => id !== ENTIRE_DISTRICT);

const SCHOOL_NAME_COLUMN = 'school_name';
const HIDDEN_COLUMNS = ['school_id'];

const omitColumns = (row: MetricRow, keys: string[]): MetricRow => {
  const visible: MetricRow = {};
  Object.keys(row).forEach((key) => {
    if (!keys.includes(key)) visible[key] = row[key];
  });
  return visible;
};

export const queryErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && typeof detail[0]?.msg === 'string') return detail[0].msg;
    return error.message;
  }
  return 'Failed to load results.';
};

interface ScopeRows {
  scope: ScopeResult;
  rows: MetricRow[];
}

export interface ScopeLayout {
  populatedScopes: ScopeRows[];
  emptyScopes: ScopeResult[];
  erroredScopes: ScopeResult[];
  useSeparateTables: boolean;
  combinedRows: MetricRow[];
}

export const layoutScopeResults = (results: QueryResults | null): ScopeLayout => {
  const scopeRows: ScopeRows[] = (results?.scopes ?? [])
    .filter((scope) => !scope.error)
    .map((scope) => ({
      scope,
      rows: metricValueToRows(scope.value, results?.columnLabel ?? '').map((row) => omitColumns(row, HIDDEN_COLUMNS)),
    }));
  const erroredScopes = (results?.scopes ?? []).filter((scope) => scope.error);

  const populatedScopes = scopeRows.filter(({ rows }) => rows.length > 0 && columnsOf(rows).length > 0);
  const emptyScopes = scopeRows
    .filter(({ rows }) => rows.length === 0 || columnsOf(rows).length === 0)
    .map(({ scope }) => scope);

  const anyScopeHasSchoolName = populatedScopes.some(({ rows }) => rows.some((row) => SCHOOL_NAME_COLUMN in row));
  const oneRowPerScope = populatedScopes.every(({ rows }) => rows.length === 1);
  const useSeparateTables = !anyScopeHasSchoolName && !oneRowPerScope && populatedScopes.length > 1;
  const isDistrictOnly = populatedScopes.length === 1 && populatedScopes[0].scope.id === ENTIRE_DISTRICT;
  const includeSchoolColumn = !anyScopeHasSchoolName && oneRowPerScope && !isDistrictOnly;
  const combinedRows: MetricRow[] = populatedScopes.flatMap(({ scope, rows }) =>
    rows.map((row) => (includeSchoolColumn ? { School: scope.label, ...row } : row))
  );

  return { populatedScopes, emptyScopes, erroredScopes, useSeparateTables, combinedRows };
};

const ChatDataExplorer = ({ eoaId, accessToken, metricOptions, request, onClose }: ChatDataExplorerProps) => {
  const {
    schools: { data: districtSchools },
  } = useSchools();
  const [metric, setMetric] = useState('');
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
  const [results, setResults] = useState<QueryResults | null>(null);
  const [isQuerying, setIsQuerying] = useState(false);

  const metricLabel = (value: string) => metricOptions.find((option) => option.value === value)?.label ?? value;

  const schoolOptions = [
    { value: ENTIRE_DISTRICT, label: ALL_SCHOOLS_LABEL },
    ...(districtSchools ?? [])
      .filter((school) => school.is_primary_on_eoa !== false)
      .map((school) => ({ value: school.id, label: school.name })),
  ];
  const schoolLabel = (value: string) => schoolOptions.find((option) => option.value === value)?.label ?? value;

  const changeMetric = (value: string) => {
    setMetric(value);
    setResults(null);
  };

  const changeSchools = (value: string[]) => {
    setSelectedSchools(value);
    setResults(null);
  };

  const handleSchoolsChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    changeSchools(resolveSchoolSelection(typeof value === 'string' ? value.split(',') : value, selectedSchools));
  };

  const runQuery = async (metricValue: string, schoolIds: string[]) => {
    const { source, name } = splitMetricValue(metricValue);
    const columnLabel = metricLabel(metricValue);
    const scopes = schoolIds.length > 0 ? schoolIds : [ENTIRE_DISTRICT];

    setIsQuerying(true);
    setResults(null);
    try {
      const scopeResults = await Promise.all(
        scopes.map(async (scope): Promise<ScopeResult> => {
          const isDistrict = scope === ENTIRE_DISTRICT;
          const label = schoolLabel(scope);
          try {
            const response = isDistrict
              ? source === 'survey'
                ? await postSurveyMetricsQuery(eoaId, accessToken, [name])
                : await postMetricsQuery(eoaId, accessToken, [name])
              : source === 'survey'
                ? await postSchoolSurveyMetricsQuery(eoaId, scope, accessToken, [name])
                : await postSchoolMetricsQuery(eoaId, scope, accessToken, [name]);
            return { id: scope, label, value: response.results[name], error: response.errors[name] };
          } catch (error) {
            return { id: scope, label, error: queryErrorMessage(error) };
          }
        })
      );
      setResults({ columnLabel, scopes: scopeResults });
    } finally {
      setIsQuerying(false);
    }
  };

  useEffect(() => {
    if (!request) return;
    setMetric(request.metric);
    setSelectedSchools([]);
    void runQuery(request.metric, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const { populatedScopes, emptyScopes, erroredScopes, useSeparateTables, combinedRows } = layoutScopeResults(results);

  const sideDrawerSlot = document.getElementById('sideDrawer');
  if (!sideDrawerSlot) return null;

  return createPortal(
    <aside className="chat-data-explorer" aria-label="Explore data">
      <div className="chat-data-explorer-header">
        <h2 className="chat-data-explorer-title">Explore data</h2>
        <button type="button" className="chat-data-explorer-close" onClick={onClose} aria-label="Close data explorer">
          <img src={CloseIcon} alt="" aria-hidden="true" />
        </button>
      </div>
      <p className="chat-data-explorer-subtitle">Pull a metric for specific schools.</p>

      <div className="chat-data-explorer-field">
        <label className="chat-data-explorer-label" id="data-explorer-metric-label">
          Metric
        </label>
        <FormControl fullWidth size="small">
          <Select
            labelId="data-explorer-metric-label"
            value={metric}
            onChange={(event: SelectChangeEvent) => changeMetric(event.target.value)}
            displayEmpty
            renderValue={(value) =>
              value ? (
                <Chip
                  label={metricLabel(value)}
                  deleteIcon={<ChipDeleteIcon />}
                  onDelete={() => changeMetric('')}
                  onMouseDown={(event) => event.stopPropagation()}
                />
              ) : null
            }
          >
            {metricOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="chat-data-explorer-field">
        <label className="chat-data-explorer-label" id="data-explorer-schools-label">
          Schools
        </label>
        <FormControl fullWidth size="small">
          <Select
            labelId="data-explorer-schools-label"
            multiple
            value={selectedSchools}
            onChange={handleSchoolsChange}
            displayEmpty
            renderValue={(selected) => (
              <div className="chat-data-explorer-chips">
                {selected.map((school) => (
                  <Chip
                    key={school}
                    label={schoolLabel(school)}
                    deleteIcon={<ChipDeleteIcon />}
                    onDelete={() => changeSchools(selectedSchools.filter((item) => item !== school))}
                    onMouseDown={(event) => event.stopPropagation()}
                  />
                ))}
              </div>
            )}
          >
            {schoolOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <CustomButton
        variant="primary"
        buttonText="Show results"
        buttonAction={() => void runQuery(metric, selectedSchools)}
        disabled={!metric}
        loading={isQuerying}
        className="chat-data-explorer-submit"
      />

      {results && (
        <div className="chat-data-explorer-results">
          {useSeparateTables
            ? populatedScopes.map(({ scope, rows }) => (
                <div key={scope.id} className="chat-data-explorer-result-block">
                  <p className="chat-data-explorer-result-label">{scope.label}</p>
                  <ChatMetricResult rows={rows} />
                  <ChatCopyButton onCopy={() => copyPlainText(rowsToTsv(rows))} label="Copy" />
                </div>
              ))
            : combinedRows.length > 0 && (
                <div className="chat-data-explorer-result-block">
                  <ChatMetricResult rows={combinedRows} />
                  <ChatCopyButton onCopy={() => copyPlainText(rowsToTsv(combinedRows))} label="Copy" />
                </div>
              )}
          {emptyScopes.map((scope) => (
            <p key={scope.id} className="chat-data-explorer-result-empty">
              {scope.label}: no data for this metric.
            </p>
          ))}
          {erroredScopes.map((scope) => (
            <p key={scope.id} className="chat-data-explorer-result-error">
              {scope.label}: {scope.error}
            </p>
          ))}
        </div>
      )}
    </aside>,
    sideDrawerSlot
  );
};

export default ChatDataExplorer;
